// src/utils/offlineDB.js

const DB_NAME = 'ArcheryNexusOffline';
const DB_VERSION = 1;

export const initDB = () => {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = (event) => reject('IndexedDB hiba: ' + event.target.errorCode);

    request.onupgradeneeded = (event) => {
      const db = event.target.result;

      // Verseny adatok tárolója
      if (!db.objectStoreNames.contains('competitions')) {
        db.createObjectStore('competitions', { keyPath: 'id' });
      }

      // Nevezők tárolója (versenyId indexszel)
      if (!db.objectStoreNames.contains('participants')) {
        const store = db.createObjectStore('participants', { keyPath: 'id' });
        store.createIndex('competitionId', 'competitionId', { unique: false });
      }

      // Függőben lévő eredmények (szinkronizáláshoz)
      if (!db.objectStoreNames.contains('pendingScores')) {
        const store = db.createObjectStore('pendingScores', { keyPath: 'id' });
        store.createIndex('competitionId', 'competitionId', { unique: false });
      }
    };

    request.onsuccess = (event) => resolve(event.target.result);
  });
};

// VERSENY ADATOK MENTÉSE
export const saveCompetitionOffline = async (competition, participants) => {
  const db = await initDB();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(['competitions', 'participants'], 'readwrite');
    const compStore = transaction.objectStore('competitions');
    const partStore = transaction.objectStore('participants');

    compStore.put(competition);

    participants.forEach(p => {
      partStore.put({ ...p, competitionId: competition.id });
    });

    transaction.oncomplete = () => resolve(true);
    transaction.onerror = () => reject(transaction.error);
  });
};

// VERSENY ADATOK LEKÉRÉSE
export const getCompetitionOffline = async (id) => {
  const db = await initDB();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(['competitions', 'participants'], 'readonly');
    const compRequest = transaction.objectStore('competitions').get(id);
    const partIndex = transaction.objectStore('participants').index('competitionId');
    const partRequest = partIndex.getAll(id);

    transaction.oncomplete = () => {
      resolve({
        competition: compRequest.result,
        participants: partRequest.result
      });
    };
    transaction.onerror = () => reject(transaction.error);
  });
};

// EREDMÉNY MENTÉSE (OFFLINE)
export const saveScoreOffline = async (competitionId, participantId, scoreData) => {
  const db = await initDB();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(['pendingScores', 'participants'], 'readwrite');
    const pendingStore = transaction.objectStore('pendingScores');
    const partStore = transaction.objectStore('participants');

    // Mentjük a függőben lévők közé
    pendingStore.put({
      id: participantId,
      competitionId,
      ...scoreData,
      timestamp: Date.now()
    });

    // Frissítjük a helyi résztvevő listát is az azonnali megjelenítéshez
    const getReq = partStore.get(participantId);
    getReq.onsuccess = () => {
      if (getReq.result) {
        partStore.put({ ...getReq.result, ...scoreData });
      }
    };

    transaction.oncomplete = () => resolve(true);
    transaction.onerror = () => reject(transaction.error);
  });
};

// FÜGGŐBEN LÉVŐ EREDMÉNYEK LEKÉRÉSE
export const getPendingScores = async (competitionId) => {
  const db = await initDB();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(['pendingScores'], 'readonly');
    const index = transaction.objectStore('pendingScores').index('competitionId');
    const request = index.getAll(competitionId);

    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
};

// SIKERES SZINKRON UTÁNI TÖRLÉS
export const clearPendingScore = async (participantId) => {
  const db = await initDB();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(['pendingScores'], 'readwrite');
    transaction.objectStore('pendingScores').delete(participantId);
    transaction.oncomplete = () => resolve(true);
    transaction.onerror = () => reject(transaction.error);
  });
};

// ÚJ NEVEZŐ RÖGZÍTÉSE (OFFLINE)
export const addParticipantOffline = async (competitionId, participantData) => {
  const db = await initDB();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(['participants', 'pendingScores'], 'readwrite');
    const partStore = transaction.objectStore('participants');
    const pendingStore = transaction.objectStore('pendingScores');

    // Generálunk egy ideiglenes ID-t ha nincs
    const tempId = participantData.id || `temp_${Date.now()}`;
    const data = {
        ...participantData,
        id: tempId,
        competitionId,
        isOfflineNew: true // Jelöljük, hogy ez egy új, még nem feltöltött nevező
    };

    partStore.put(data);
    pendingStore.put({ ...data, type: 'new_participant' });

    transaction.oncomplete = () => resolve(data);
    transaction.onerror = () => reject(transaction.error);
  });
};

// NEVEZŐ ADATAINAK FRISSÍTÉSE (OFFLINE - PL. CSAPATBEOSZTÁS, LÁNCOLÁS)
export const updateParticipantOffline = async (participantId, updateData) => {
  const db = await initDB();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(['participants', 'pendingScores'], 'readwrite');
    const partStore = transaction.objectStore('participants');
    const pendingStore = transaction.objectStore('pendingScores');

    const request = partStore.get(participantId);
    request.onsuccess = () => {
      if (request.result) {
        const updatedParticipant = { ...request.result, ...updateData };
        partStore.put(updatedParticipant);

        // Ha nem ideiglenes (temp_) ID, akkor felvesszük a szinkronizálandó listára
        if (!participantId.toString().startsWith('temp_')) {
          pendingStore.put({
            id: participantId,
            competitionId: updatedParticipant.competitionId,
            type: 'update_participant',
            updateData: updateData,
            timestamp: Date.now()
          });
        }
      }
    };

    transaction.oncomplete = () => resolve(true);
    transaction.onerror = () => reject(transaction.error);
  });
};

// NEVEZŐ TÖRLÉSE (OFFLINE)
export const deleteParticipantOffline = async (participantId) => {
  const db = await initDB();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(['participants', 'pendingScores'], 'readwrite');
    const partStore = transaction.objectStore('participants');
    const pendingStore = transaction.objectStore('pendingScores');

    partStore.delete(participantId);

    // Ha ideiglenes volt, töröljük a függőben lévő műveletek közül is
    if (participantId.toString().startsWith('temp_')) {
      pendingStore.delete(participantId);
    } else {
      // Ha már szerveres ID, akkor feljegyzünk egy törlési szándékot a későbbi szinkronhoz
      pendingStore.put({ id: participantId, type: 'delete_participant', timestamp: Date.now() });
    }

    transaction.oncomplete = () => resolve(true);
    transaction.onerror = () => reject(transaction.error);
  });
};

// ÖSSZES OFFLINE VERSENY LEKÉRÉSE (Dashboardhoz)
export const getAllCompetitionsOffline = async () => {
  const db = await initDB();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(['competitions'], 'readonly');
    const store = transaction.objectStore('competitions');
    const request = store.getAll();

    request.onsuccess = () => resolve(request.result || []);
    request.onerror = () => reject(request.error);
  });
};

// VERSENYEK GYORSÍTÓTÁRAZÁSA (AUTOMATA MENTÉS)
export const cacheCompetitions = async (competitions) => {
  const db = await initDB();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(['competitions'], 'readwrite');
    const store = transaction.objectStore('competitions');

    competitions.forEach(comp => {
      store.put(comp);
    });

    transaction.oncomplete = () => resolve(true);
    transaction.onerror = () => reject(transaction.error);
  });
};

// HELYI NEVEZŐK SZÁMÁNAK LEKÉRÉSE (Gomb állapotához)
export const getOfflineParticipantsCount = async (competitionId) => {
  const db = await initDB();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(['participants'], 'readonly');
    const index = transaction.objectStore('participants').index('competitionId');
    const request = index.count(competitionId);

    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
};
